import React, { useContext, useMemo } from 'react';
import { RouteObject } from 'react-router-dom';

import { articleApi } from '@/modules/articles/api';
import {
  NewsWidget,
  WidgetHost,
  WidgetContainerContext,
  WidgetContainer,
  WidgetLink,
  WidgetHostContext,
} from '@/core/widgets/export';
import { AnalyticsSubTypeEvent } from '@/shared/analytics/export';
import PinnedArticlePage from '@/modules/articles/pages/pinned-article-page';
import { qCode } from '@/modules/articles/model/concept-item';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNewspaper } from '@fortawesome/free-solid-svg-icons';
import PinnedWidgetMenuActions from '@/modules/articles/components/pins/pinned-widget-menu-actions';
import { comingSoonQCodes } from './coming-soon-series';
import PinnedSeriesList from '@/modules/articles/components/pins/pinned-series-list';
import { PinnedSeriesWidgetProvider } from '@/modules/articles/contexts/pinned-series-widget-context';

export interface PinnedWidgetState {
  title?: string;
  currentPage?: number;
}

export interface PinnedWidgetPreferences {
  color?: string;
}

function useWidgetCurrentPage() {
  const widgetContainer = useContext<WidgetContainer>(WidgetContainerContext);

  const currentPage = widgetContainer.getState<PinnedWidgetState>()?.currentPage ?? 0;

  const setCurrentPage = (page: number) => {
    widgetContainer.updateState<PinnedWidgetState>({ currentPage: page });
  };

  return { currentPage, setCurrentPage };
}

export class PinnedSeriesWidget implements NewsWidget<PinnedWidgetState, PinnedWidgetPreferences> {
  constructor(
    private genre: string,
    private comingSoon: boolean,
    private customTitle?: string,
  ) {}

  get defaultRoute(): string {
    return '';
  }

  get defaultTitle(): string {
    return this.customTitle || 'Pinned Series';
  }

  async hasPermission() {
    return true;
  }

  get headerPanel(): JSX.Element {
    return (
      <WidgetContainerContext.Consumer>
        {widgetContainer => (
          <>
            <span className={'w-100 position-relative'}>
              <FontAwesomeIcon className="align-text-bottom" icon={faNewspaper} />{' '}
              {widgetContainer.getState<PinnedWidgetState>()?.title ?? this.defaultTitle}
            </span>
            <span
              className={'position-absolute  w-100 text-center '}
              style={{ top: 0, left: 0, zIndex: 100, pointerEvents: 'none' }}
            >
              <img src={'/pin.svg'} width={'50px'} alt={'pin'} />
            </span>
          </>
        )}
      </WidgetContainerContext.Consumer>
    );
  }

  headerActions({ children }: { children?: React.ReactNode }): JSX.Element {
    return <>{children}</>;
  }

  menuActions(close: () => void): React.ReactNode {
    return (
      <PinnedWidgetMenuActions
        close={close}
        defaultColor="note-yellow"
        colorChangeEvent={AnalyticsSubTypeEvent.PINNED_SERIES_COLOR_CHANGE}
      />
    );
  }

  get id(): string {
    return '/pinned-series/' + this.genre;
  }

  initialize(host: WidgetHost): void {}
  containerClass(state?: PinnedWidgetState, preferences?: PinnedWidgetPreferences) {
    return `${preferences?.color ?? 'note-yellow'} sticky-note shadow-sm`;
  }

  get routes(): RouteObject[] {
    return [
      {
        path: '/',
        element: (
          <WidgetContainerContext.Consumer>
            {widgetContainer => (
              <WidgetHostContext.Consumer>
                {widgetHost => {
                  return (
                    <PinnedSeriesWidgetProvider widgetHost={widgetHost}>
                      {pinnedSeriesWidgetContent => {
                        return (
                          <PinnedArticlePage
                            className={
                              pinnedSeriesWidgetContent.isStickyNote
                                ? (widgetContainer.getPreferences<PinnedWidgetPreferences>()
                                    ?.color ?? 'note-yellow')
                                : 'text-white'
                            }
                            widgetLink={
                              <WidgetLink
                                to="/list"
                                className={
                                  pinnedSeriesWidgetContent.isStickyNote ? 'fs-5' : 'fs-2 text-end'
                                }
                              >
                                History
                              </WidgetLink>
                            }
                          />
                        );
                      }}
                    </PinnedSeriesWidgetProvider>
                  );
                }}
              </WidgetHostContext.Consumer>
            )}
          </WidgetContainerContext.Consumer>
        ),
        loader: async () => {
          try {
            const articlePage = await articleApi.getArticlesBySection(undefined, {
              limit: 1,
              includeGenres: [this.genre],
            });

            if (articlePage.content.length < 1) {
              let title = this.defaultTitle;
              if (this.comingSoon) {
                title = comingSoonQCodes.find(c => c.qCode === this.genre)?.name ?? title;
              }
              return {
                article: undefined,
                title,
                genre: this.genre,
                comingSoon: this.comingSoon,
                loaded: true,
              };
            }

            const article = articlePage.content[0];

            return {
              article,
              title: article.genre.find(value => qCode(value) === this.genre)?.name,
              genre: this.genre,
              loaded: true,
            };
          } catch (e) {
            return {
              article: undefined,
              title: this.defaultTitle,
              genre: this.genre,
              loaded: true,
            };
          }
        },
      },
      {
        path: '/list',
        element: (
          <WidgetHostContext.Consumer>
            {widgetHost => (
              <PinnedSeriesWidgetProvider widgetHost={widgetHost}>
                {pinnedSeriesWidgetContent => {
                  const { currentPage, setCurrentPage } = useWidgetCurrentPage();
                  const genre = this.genre;
                  const series = useMemo(() => [genre], [genre]);
                  return (
                    <PinnedSeriesList
                      series={series}
                      isStickyNote={pinnedSeriesWidgetContent.isStickyNote}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                    />
                  );
                }}
              </PinnedSeriesWidgetProvider>
            )}
          </WidgetHostContext.Consumer>
        ),
      },
      {
        path: '/list/:id',
        element: (
          <WidgetContainerContext.Consumer>
            {widgetContainer => (
              <WidgetHostContext.Consumer>
                {widgetHost => {
                  return (
                    <PinnedSeriesWidgetProvider widgetHost={widgetHost}>
                      {pinnedSeriesWidgetContent => {
                        return (
                          <PinnedArticlePage
                            className={
                              pinnedSeriesWidgetContent.isStickyNote
                                ? (widgetContainer.getPreferences<PinnedWidgetPreferences>()
                                    ?.color ?? 'note-yellow')
                                : 'text-white'
                            }
                            widgetLink={
                              <div className="d-flex justify-content-between">
                                <WidgetLink
                                  to="/list"
                                  className={
                                    pinnedSeriesWidgetContent.isStickyNote ? 'fs-5' : 'fs-2 ms-3'
                                  }
                                >
                                  Back to list
                                </WidgetLink>
                                <WidgetLink
                                  to="/"
                                  className={
                                    pinnedSeriesWidgetContent.isStickyNote ? 'fs-5' : 'fs-2'
                                  }
                                >
                                  Show latest version
                                </WidgetLink>
                              </div>
                            }
                          />
                        );
                      }}
                    </PinnedSeriesWidgetProvider>
                  );
                }}
              </WidgetHostContext.Consumer>
            )}
          </WidgetContainerContext.Consumer>
        ),
        loader: async ({ params }) => {
          try {
            const articlePage = await articleApi.getArticle(params.id || '');

            const article = articlePage;

            return {
              article,
              title: article.genre.find(value => qCode(value) === this.genre)?.name,
              genre: this.genre,
              loaded: true,
            };
          } catch (e) {
            return {
              article: undefined,
              title: this.defaultTitle,
              genre: this.genre,
              loaded: true,
            };
          }
        },
      },
    ];
  }

  get shortTitle(): JSX.Element {
    return <>Test</>;
  }
}
